import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import {
  ActivityDomains,
  appendActivity,
  type ActivityActor,
  type ActivityAppendInput,
} from "./activityService";
import { firestore } from "./firebase_config";
import { normalizeUserRole, UserRole, type UserProfile } from "./roles";

/**
 * User service for managing user profiles and roles in Firestore
 */

async function logActivitySafe(entry: ActivityAppendInput): Promise<void> {
  try {
    await appendActivity(entry);
  } catch (e) {
    console.error("Failed to record activity:", e);
  }
}

// Convert Firestore Timestamp to number for UserProfile
const timestampToNumber = (timestamp: any): number => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toMillis();
  }
  if (typeof timestamp === "number") {
    return timestamp;
  }
  return Date.now();
};

/**
 * Check if an email is in the admin list
 */
export async function isAdminEmail(email: string): Promise<boolean> {
  try {
    const adminEmailDoc = doc(firestore, "config", "adminEmails");
    const snapshot = await getDoc(adminEmailDoc);

    if (snapshot.exists()) {
      const data = snapshot.data();
      return data[email] === true;
    }
    return false;
  } catch (error) {
    console.error("Error checking admin email:", error);
    return false;
  }
}

/**
 * Create a new user profile in Firestore
 */
export async function createUserProfile(
  uid: string,
  email: string,
  displayName?: string,
  photoURL?: string,
): Promise<UserProfile> {
  try {
    // Check if user is admin
    const isAdmin = await isAdminEmail(email);
    const role = isAdmin ? UserRole.ADMIN : UserRole.VIEWER;

    const now = Date.now();
    const userProfile: UserProfile = {
      email,
      role,
      displayName: displayName || email.split("@")[0],
      createdAt: now,
      lastLogin: now,
      isActive: true,
    };

    // Create user document in Firestore
    const userRef = doc(firestore, "users", uid);
    await setDoc(userRef, userProfile);

    console.log(`User profile created for ${email} with role: ${role}`);
    return userProfile;
  } catch (error) {
    console.error("Error creating user profile:", error);
    throw error;
  }
}

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  try {
    const userRef = doc(firestore, "users", uid);
    const snapshot = await getDoc(userRef);

    if (snapshot.exists()) {
      const data = snapshot.data();
      return {
        ...data,
        createdAt: timestampToNumber(data.createdAt),
        lastLogin: timestampToNumber(data.lastLogin),
        role: normalizeUserRole(data.role as string | undefined),
      } as UserProfile;
    }
    return null;
  } catch (error) {
    console.error("Error getting user profile:", error);
    return null;
  }
}

/**
 * Update user's last login timestamp
 */
export async function updateLastLogin(uid: string): Promise<void> {
  try {
    const userRef = doc(firestore, "users", uid);
    await updateDoc(userRef, {
      lastLogin: Date.now(),
    });
  } catch (error) {
    console.error("Error updating last login:", error);
  }
}

/**
 * Update user role (admin only - enforced by security rules)
 */
export async function updateUserRole(
  targetUid: string,
  newRole: UserRole,
  actor?: ActivityActor | null,
): Promise<void> {
  try {
    const before = actor ? await getUserProfile(targetUid) : null;
    const userRef = doc(firestore, "users", targetUid);
    await updateDoc(userRef, {
      role: newRole,
    });
    console.log(`User role updated to: ${newRole}`);
    if (actor) {
      const after = await getUserProfile(targetUid);
      await logActivitySafe({
        domain: ActivityDomains.Users,
        action: "update",
        resourceType: "user_role",
        resourceId: targetUid,
        summary: before
          ? `Changed role for ${before.email} to ${newRole}`
          : `Changed role for user ${targetUid} to ${newRole}`,
        actor,
        details: {
          beforeRole: before?.role,
          afterRole: after?.role ?? newRole,
        },
      });
    }
  } catch (error) {
    console.error("Error updating user role:", error);
    throw error;
  }
}

/**
 * Update user active status (admin only)
 */
export async function updateUserActiveStatus(
  targetUid: string,
  isActive: boolean,
  actor?: ActivityActor | null,
): Promise<void> {
  try {
    const before = actor ? await getUserProfile(targetUid) : null;
    const userRef = doc(firestore, "users", targetUid);
    await updateDoc(userRef, {
      isActive,
    });
    console.log(`User active status updated to: ${isActive}`);
    if (actor) {
      await logActivitySafe({
        domain: ActivityDomains.Users,
        action: "update",
        resourceType: "user_active_status",
        resourceId: targetUid,
        summary: before
          ? `${isActive ? "Activated" : "Deactivated"} ${before.email}`
          : `Updated active status for user ${targetUid}`,
        actor,
        details: {
          beforeActive: before?.isActive,
          afterActive: isActive,
        },
      });
    }
  } catch (error) {
    console.error("Error updating user active status:", error);
    throw error;
  }
}

/**
 * Get all users (admin only - enforced by security rules)
 */
export async function getAllUsers(): Promise<Record<string, UserProfile>> {
  try {
    const usersCollection = collection(firestore, "users");
    const snapshot = await getDocs(usersCollection);

    const users: Record<string, UserProfile> = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      users[doc.id] = {
        ...data,
        createdAt: timestampToNumber(data.createdAt),
        lastLogin: timestampToNumber(data.lastLogin),
        role: normalizeUserRole(data.role as string | undefined),
      } as UserProfile;
    });

    return users;
  } catch (error) {
    console.error("Error getting all users:", error);
    throw error;
  }
}

/**
 * Initialize or get user profile (called on login)
 */
export async function initializeUserProfile(
  uid: string,
  email: string,
  displayName?: string,
  photoURL?: string,
): Promise<UserProfile> {
  try {
    // Check if user already exists
    let profile = await getUserProfile(uid);

    if (profile) {
      // User exists, update last login
      await updateLastLogin(uid);
      return profile;
    } else {
      // New user, create profile
      profile = await createUserProfile(uid, email, displayName, photoURL);
      return profile;
    }
  } catch (error) {
    console.error("Error initializing user profile:", error);
    throw error;
  }
}
